import React, { useState, useMemo, useEffect, useRef } from 'react';
import styles from './ProductPicker.module.css';

export interface PickerVariant {
  id: number;
  name: string;
  price: number;
  stock: number;
  option1Value?: string;
  option2Value?: string;
  option3Value?: string;
}

export interface PickerProduct {
  id: number;
  name: string;
  category: string;
  subCategory: string;
  image: string;
  price: number;
  discountPrice: number | null;
  prixAchat: number | null;
  stock: number;
  variants?: PickerVariant[];
}

interface Props {
  products: PickerProduct[];
  onSelect: (p: PickerProduct) => void;
  onClose: () => void;
}

const fmt = (v: number) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v);

const variantLabel = (v: PickerVariant) => {
  const parts = [v.name, v.option1Value, v.option2Value, v.option3Value].filter(Boolean);
  return parts.join(' · ');
};

export const ProductPicker: React.FC<Props> = ({ products, onSelect, onClose }) => {
  const [search, setSearch]       = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => { searchRef.current?.focus(); }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const categories = useMemo(() => {
    const set = new Set(products.map(p => p.category).filter(Boolean));
    return Array.from(set).sort();
  }, [products]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return products.filter(p => {
      const matchCat = !filterCat || p.category === filterCat;
      const matchSearch = !q ||
        p.name.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.subCategory?.toLowerCase().includes(q);
      return matchCat && matchSearch;
    });
  }, [products, search, filterCat]);

  const handleProductClick = (p: PickerProduct) => {
    const hasVariants = p.variants && p.variants.length > 0;
    if (!hasVariants) {
      onSelect(p);
      onClose();
    } else {
      setExpandedId(prev => prev === p.id ? null : p.id);
    }
  };

  const handleVariantClick = (p: PickerProduct, v: PickerVariant) => {
    const label = variantLabel(v);
    onSelect({
      ...p,
      name: label ? `${p.name} — ${label}` : p.name,
      price: v.price,
      discountPrice: null,
      stock: v.stock,
    });
    onClose();
  };

  return (
    <div className={styles.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={styles.modal}>

        <div className={styles.header}>
          <h2 className={styles.title}>Choisir un produit</h2>
          <span className={styles.count}>{filtered.length} / {products.length}</span>
          <button className={styles.btnClose} onClick={onClose}>✕</button>
        </div>

        <div className={styles.toolbar}>
          <div className={styles.searchWrap}>
            <span className={styles.searchIcon}>🔍</span>
            <input
              ref={searchRef}
              type="text"
              className={styles.searchInput}
              placeholder="Rechercher par nom, catégorie..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && <button className={styles.btnClear} onClick={() => setSearch('')}>✕</button>}
          </div>
          <div className={styles.cats}>
            <button className={`${styles.cat} ${filterCat === '' ? styles.catActive : ''}`} onClick={() => setFilterCat('')}>
              Tous
            </button>
            {categories.map(cat => (
              <button
                key={cat}
                className={`${styles.cat} ${filterCat === cat ? styles.catActive : ''}`}
                onClick={() => setFilterCat(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.listWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.colImg}></th>
                <th>Produit</th>
                <th className={styles.colCat}>Catégorie</th>
                <th className={styles.colPrice}>Prix TTC</th>
                <th className={styles.colPrice}>PA HT</th>
                <th className={styles.colStock}>Stock</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const prix = p.discountPrice ?? p.price;
                const hasVariants = p.variants && p.variants.length > 0;
                const isExpanded = expandedId === p.id;

                return (
                  <React.Fragment key={p.id}>
                    {/* Ligne produit */}
                    <tr
                      className={`${styles.row} ${isExpanded ? styles.rowExpanded : ''}`}
                      onClick={() => handleProductClick(p)}
                    >
                      <td className={styles.colImg}>
                        {p.image
                          ? <img src={p.image} alt="" className={styles.thumb} onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                          : <div className={styles.noImg}>📦</div>
                        }
                      </td>
                      <td>
                        <div className={styles.prodName}>
                          {p.name}
                          {hasVariants && (
                            <span className={styles.variantToggle}>
                              {isExpanded ? ' ▲' : ' ▼'} {p.variants!.length} variante{p.variants!.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        {p.subCategory && <div className={styles.sub}>{p.subCategory}</div>}
                      </td>
                      <td className={styles.colCat}>
                        <span className={styles.catBadge}>{p.category}</span>
                      </td>
                      <td className={`${styles.colPrice} ${styles.priceVal}`}>
                        {hasVariants ? <span className={styles.muted}>—</span> : fmt(prix)}
                      </td>
                      <td className={`${styles.colPrice} ${styles.paVal}`}>
                        {p.prixAchat != null ? fmt(p.prixAchat) : '—'}
                      </td>
                      <td className={styles.colStock}>
                        <span className={p.stock <= 0 ? styles.stockEmpty : p.stock <= 5 ? styles.stockLow : styles.stockOk}>
                          {p.stock}
                        </span>
                      </td>
                    </tr>

                    {/* Lignes variantes (expand) */}
                    {isExpanded && p.variants!.map(v => (
                      <tr
                        key={`v-${v.id}`}
                        className={styles.variantRow}
                        onClick={() => handleVariantClick(p, v)}
                      >
                        <td className={styles.colImg}></td>
                        <td>
                          <div className={styles.variantName}>↳ {variantLabel(v)}</div>
                        </td>
                        <td className={styles.colCat}></td>
                        <td className={`${styles.colPrice} ${styles.priceVal}`}>{fmt(v.price)}</td>
                        <td className={`${styles.colPrice} ${styles.paVal}`}>—</td>
                        <td className={styles.colStock}>
                          <span className={v.stock <= 0 ? styles.stockEmpty : v.stock <= 5 ? styles.stockLow : styles.stockOk}>
                            {v.stock}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className={styles.noResult}>Aucun produit trouvé.</td></tr>
              )}
            </tbody>
          </table>
        </div>

      </div>
    </div>
  );
};
